import { Box, Grid, Typography, FormHelperText, Stack, SxProps, Theme } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';
import { AnnouncementState, selectAnnouncementsState } from '../sagas/announcementSlice';
import { AnnouncementFrequencies } from '@inosio/payments-sdk';
import { useEffect, useRef } from 'react';
import { AnnouncementSectionProps } from '../validators/AnnouncementValidators';
import { parseISO, compareAsc } from 'date-fns';

const schemaValidation = Yup.object({
  Frequency: Yup.string().required('Schedule type is required'),
  FirstSendDateTime: Yup.date()
    .nullable()
    .required('Send date and time is required')
    .test('not-less-than-now', 'Time cannot be in the past', function (value) {
      if (!value) return true;
      const currentDateTime = new Date();
      return compareAsc(value, currentDateTime) === 1;
    }),
});

type schemaValidationType = {
  Frequency: AnnouncementFrequencies;
  FirstSendDateTime: Date | null;
};

interface IProps extends AnnouncementSectionProps {}

export const OneTimeSchedule = (props: IProps) => {
  const isInitialRender = useRef<boolean>(true);
  const { announcementDetail }: AnnouncementState = useSelector(selectAnnouncementsState);

  const formik = useFormik<schemaValidationType>({
    initialValues: {
      Frequency: announcementDetail.Frequency || AnnouncementFrequencies.OneTime,
      FirstSendDateTime: announcementDetail.FirstSendDatetime ? parseISO(announcementDetail.FirstSendDatetime) : null,
    },
    validationSchema: schemaValidation,
    onSubmit: (values) => {},
  });

  useEffect(() => {
    if (isInitialRender.current) {
      if (formik.values.FirstSendDateTime) {
        formik.validateForm();
      }
      isInitialRender.current = false;
    }
  }, [formik.values.FirstSendDateTime, formik]);

  const handleChangeSendDateTime = (dateTime: Date | null) => {
    formik
      .setFieldValue('FirstSendDateTime', dateTime)
      .then((possibleErrors) => {
        if (dateTime && !possibleErrors?.FirstSendDateTime) {
          props.setPendingAnnouncement({
            FirstSendDatetime: dateTime.toISOString(),
            Frequency: formik.values.Frequency,
          });
        }
      })
      .catch((err) => {});
  };

  useEffect(() => {
    if ((!announcementDetail.Frequency || announcementDetail.Frequency !== AnnouncementFrequencies.OneTime) && announcementDetail.FirstSendDatetime) {
      const frequency = AnnouncementFrequencies.OneTime;
      formik.setFieldValue('Frequency', frequency).then((possibleErrors) => {
        if (!possibleErrors?.Frequency) {
          props.setPendingAnnouncement({
            Frequency: frequency,
          });
        }
      });
    }
  }, [announcementDetail]);

  const textBaseStyle: SxProps<Theme> = {
    color: '#000000',
    fontFamily: 'Work Sans',
    lineHeight: '22px',
  };

  const sxStyle = {
    borderRadius: '24px',
    minWidth: '320px',
    '& .MuiInputBase-root': {
      height: '40px',
      borderRadius: '24px',
      '& input': {
        height: '40px',
        padding: '0 14px',
        lineHeight: '40px',
      },
    },
  };

  return (
    <Box sx={{ padding: 3 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography sx={{ ...textBaseStyle, fontWeight: '600', fontSize: '18px' }}>Schedule Sending:</Typography>
        </Grid>
        {/* Date Field */}
        <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'row' }} gap={1}>
          <Typography variant="body1" sx={{ ...textBaseStyle, marginTop: '8px' }}>
            Date:
          </Typography>
          <Grid item xs={12}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                value={formik.values?.FirstSendDateTime}
                onChange={handleChangeSendDateTime}
                format="MM/dd/yyyy"
                sx={sxStyle}
                disablePast
                slotProps={{
                  textField: {
                    onKeyDown: (e: any) => {
                      e.preventDefault();
                    },
                    onPaste: (e: any) => {
                      e.preventDefault();
                    },
                  },
                  popper: {
                    sx: {
                      '.MuiPaper-root': {
                        borderRadius: '24px',
                        marginTop: '10px',
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>
        </Grid>
        {/* Time Field */}
        <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'row' }} gap={1}>
          <Typography variant="body1" sx={{ ...textBaseStyle, marginTop: '8px' }}>
            Time:
          </Typography>
          <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'row' }}>
            <Stack>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <TimePicker
                  value={formik.values.FirstSendDateTime}
                  onAccept={handleChangeSendDateTime}
                  sx={sxStyle}
                  disablePast={false}
                  timeSteps={{ hours: 1, minutes: 30 }}
                  slotProps={{
                    textField: {
                      onKeyDown: (e: any) => {
                        e.preventDefault();
                      },
                      onPaste: (e: any) => {
                        e.preventDefault();
                      },
                    },
                    popper: {
                      sx: {
                        '.MuiPaper-root': {
                          borderRadius: '24px',
                          marginTop: '10px',
                          paddingTop: '10px',
                          '.MuiMultiSectionDigitalClock-root': {
                            width: '320px',
                            justifyContent: 'space-evenly',
                          },
                          '.MuiMultiSectionDigitalClockSection-item.Mui-selected': {
                            backgroundColor: '#39A2DB',
                          },
                          '.MuiMultiSectionDigitalClockSection-root': {
                            width: '100%',
                            display: 'flex',

                            flexDirection: 'column',
                            alignItems: 'center',
                          },
                        },
                      },
                    },
                  }}
                />
                {formik.errors.FirstSendDateTime && <FormHelperText error>{formik.errors.FirstSendDateTime}</FormHelperText>}
              </LocalizationProvider>
            </Stack>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};
